"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  BookOpen, GraduationCap, Headphones, FileText, ArrowRight, ArrowDown,
  MessageSquare, Sparkles, Globe, Database, Server, Cloud,
  Shield, Cpu, Radio, CreditCard, Layers, MapPin,
  Users, TrendingUp, Zap, ChevronRight, ExternalLink,
  Github, FileCode2, Target, UserCheck, Mic, Calendar,
  BookMarked, Lightbulb, Award, BarChart3, Rocket, ShoppingCart, Download
} from "lucide-react";

// ====== ANIMACOES ======
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const cardV = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

// ====== TIPOS ======
interface CourseData { slug: string; title: string; modules: number; lessons: number; audio: number; }
interface CourseHours { audioH: string; leituraH: string; exerciciosH: string; totalH: string; }

// Carga horaria calculada com ffprobe (audio real) + estimativa leitura (200 palavras/min) + 5min exercicio/aula
const COURSE_HOURS: Record<string, CourseHours> = {
  "arquitetura-produtos-educacionais": { audioH: "11h52", leituraH: "7h30", exerciciosH: "5h35", totalH: "25h" },
  "vibecoding-pro": { audioH: "3h19", leituraH: "4h20", exerciciosH: "3h40", totalH: "11h19" },
  "pre-vibecoding": { audioH: "2h22", leituraH: "1h32", exerciciosH: "2h30", totalH: "6h24" },
  "funil-evergreen-fundamentos": { audioH: "4h10", leituraH: "2h00", exerciciosH: "2h00", totalH: "8h10" },
  "funil-evergreen-pratica": { audioH: "4h40", leituraH: "2h20", exerciciosH: "2h00", totalH: "9h00" },
};
interface FlowData { name: string; slug: string; steps: number; credits_cost: number; active: boolean; }
interface BlogData { title: string; slug: string; created_at: string; }
interface Stats { lessons: number; articles: number; documents: number; executions: number; }

// ====== DADOS ======
const JOURNEY_STEPS = [
  { phase: "INPUT", label: "Atração", icon: Target, color: "#3B82F6", desc: "Blog SEO, eBook, redes sociais" },
  { phase: "GATEKEEPER", label: "Qualificação", icon: UserCheck, color: "#F59E0B", desc: "Evento, entrevista com Jhow" },
  { phase: "BLACK BOX", label: "Transformação", icon: Lightbulb, color: "#8B5CF6", desc: "Mentoria APE, cursos, fluxos IA" },
  { phase: "OUTPUT", label: "Resultado", icon: Award, color: "#10B981", desc: "Produto educacional validado" },
  { phase: "FEEDBACK", label: "Melhoria", icon: BarChart3, color: "#EF4444", desc: "NPS, case studies, iteração" },
];

const PROJECTS = [
  {
    name: "eBuz", desc: "Plataforma SaaS de produtos educacionais com IA, cursos, fluxos e blog",
    url: "https://app.ebuz.com.br",
    stack: ["Next.js 15", "React 19", "Supabase", "Vercel"],
    stats: [{ l: "Aulas", v: "141" }, { l: "Cursos", v: "3" }, { l: "Artigos", v: "29" }],
    color: "#7C3AED", icon: GraduationCap,
  },
  {
    name: "Negócios S/A", desc: "Networking com matching IA, 7 ferramentas TRACKS, mapa e chat",
    url: "https://app.antoniofogaca.com.br",
    stack: ["React 18", "Vite", "Express", "Hetzner"],
    stats: [{ l: "Usuários", v: "125+" }, { l: "Ferramentas", v: "7" }, { l: "Exercícios", v: "45" }],
    color: "#D4A843", icon: Users,
  },
  {
    name: "Fonte das Licitações", desc: "Monitoramento de licitações públicas com IA, BPO e curso",
    url: "https://fontedaslicitacoes.com.br",
    stack: ["React 18", "Vite", "Express", "Supabase"],
    stats: [{ l: "Licitações", v: "809" }, { l: "Ferramentas", v: "4" }, { l: "Aulas", v: "24" }],
    color: "#10B981", icon: Globe,
  },

];

const RESEARCHERS = [
  { name: "Polanyi", node: "INPUT", desc: "Conhecimento tácito", href: "/blog/conhecimento-tacito-experts-nao-conseguem-ensinar" },
  { name: "Knowles", node: "INPUT", desc: "Andragogia", href: "/blog/andragogia-aprendizagem-adultos" },
  { name: "Schwartz", node: "GATEKEEPER", desc: "Níveis de consciência", href: "/blog/niveis-consciencia-schwartz-marketing" },
  { name: "Rackham", node: "GATEKEEPER", desc: "SPIN Selling", href: "/blog/spin-selling-metodo-vendas-complexas" },
  { name: "Bloom", node: "BLACK BOX", desc: "Taxonomia cognitiva", href: "/blog/taxonomia-de-bloom-guia-completo" },
  { name: "Gagne", node: "BLACK BOX", desc: "9 eventos instrucionais", href: "/blog/design-instrucional-guia-completo" },
  { name: "Ericsson", node: "BLACK BOX", desc: "Prática deliberada", href: "/blog/mito-10000-horas-pratica-deliberada" },
  { name: "Sweller", node: "BLACK BOX", desc: "Carga cognitiva", href: "/blog/carga-cognitiva-o-que-e-como-reduzir" },
  { name: "Glasser", node: "BLACK BOX", desc: "Pirâmide de retenção", href: "/blog/piramide-de-aprendizagem-retencao" },
  { name: "Wiggins", node: "OUTPUT", desc: "Backward Design", href: "/blog/backward-design-metodo-criar-cursos" },
  { name: "Murphy", node: "FEEDBACK", desc: "Customer Success", href: "/blog/customer-success-educacao-resultado-aluno" },
  { name: "Reichheld", node: "FEEDBACK", desc: "NPS", href: "/blog/nps-educacao-como-medir-satisfacao-aluno" },
];

const INFRA = [
  { name: "Vercel", desc: "Deploy automático, CDN global", icon: Cloud, color: "text-gray-700" },
  { name: "Supabase", desc: "PostgreSQL + Auth + RLS + Storage", icon: Database, color: "text-emerald-600" },
  { name: "Hetzner VPS", desc: "Ollama, backups, serviços auxiliares", icon: Server, color: "text-red-500" },
  { name: "AI Router", desc: "Claude > Groq > Gemini > Ollama", icon: Cpu, color: "text-violet-600" },
  { name: "Google TTS", desc: "Chirp3-HD audio premium", icon: Headphones, color: "text-cyan-600" },
  { name: "Twilio", desc: "SMS e verificação", icon: Radio, color: "text-red-600" },
  { name: "Assas", desc: "Checkout transparente (cartão, PIX, boleto)", icon: CreditCard, color: "text-blue-600" },
  { name: "Redis", desc: "Cache e rate limiting", icon: Zap, color: "text-orange-500" },
];

// ====== COMPONENTES ======
function Section({ id, number, title, desc, label, bgIcon: BgIcon, children }: { id?: string; number: string; title: string; desc: string; label?: string; bgIcon?: any; children: React.ReactNode }) {
  const sectionStyles: Record<string, { color: string; gradient: string; border: string; iconColor: string; badgeBg: string; topBorder: string }> = {
    "00": {
      color: "#7C3AED",
      gradient: "from-violet-50 via-white to-violet-100",
      border: "border-l-violet-600",
      iconColor: "text-violet-400",
      badgeBg: "bg-violet-600",
      topBorder: "from-violet-400 via-violet-300 to-transparent",
    },
    "01": {
      color: "#3B82F6",
      gradient: "from-blue-50 via-white to-indigo-50",
      border: "border-l-indigo-500",
      iconColor: "text-blue-400",
      badgeBg: "bg-indigo-500",
      topBorder: "from-indigo-400 via-blue-300 to-transparent",
    },
    "02": {
      color: "#F59E0B",
      gradient: "from-amber-50 via-white to-orange-50",
      border: "border-l-amber-500",
      iconColor: "text-amber-400",
      badgeBg: "bg-amber-500",
      topBorder: "from-amber-400 via-orange-300 to-transparent",
    },
    "03": {
      color: "#8B5CF6",
      gradient: "from-violet-100 via-violet-50 to-white",
      border: "border-l-violet-700",
      iconColor: "text-violet-400",
      badgeBg: "bg-violet-700",
      topBorder: "from-violet-500 via-purple-300 to-transparent",
    },
    "04": {
      color: "#10B981",
      gradient: "from-emerald-50 via-white to-teal-50",
      border: "border-l-emerald-500",
      iconColor: "text-emerald-400",
      badgeBg: "bg-emerald-500",
      topBorder: "from-emerald-400 via-teal-300 to-transparent",
    },
    "05": {
      color: "#6B7280",
      gradient: "from-gray-50 via-white to-slate-50",
      border: "border-l-gray-500",
      iconColor: "text-gray-400",
      badgeBg: "bg-gray-500",
      topBorder: "from-gray-400 via-slate-300 to-transparent",
    },
  };
  const style = sectionStyles[number] || sectionStyles["00"];

  return (
    <motion.section id={id} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
      {/* Header Card */}
      <div className={`group relative overflow-hidden rounded-xl border border-gray-200 border-l-4 ${style.border} bg-gradient-to-r ${style.gradient} p-6 mb-4 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow`}>
        {/* Gradient top accent */}
        <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${style.topBorder}`} />
        {/* Mesh gradient overlay */}
        <div className="absolute inset-0 opacity-30" style={{ background: `radial-gradient(ellipse at 80% 20%, ${style.color}10 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, ${style.color}08 0%, transparent 50%)` }} />
        {/* Background icon */}
        {BgIcon && (
          <div className={`absolute right-4 top-1/2 -translate-y-1/2 ${style.iconColor} opacity-[0.25]`}>
            <BgIcon size={90} strokeWidth={1.2} />
          </div>
        )}
        <div className="relative z-10 flex items-start gap-4">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white ${style.badgeBg} shadow-sm`}>
            {number}
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500 mt-1 leading-relaxed max-w-2xl">{desc}</p>
          </div>
        </div>
      </div>
      {/* Category label */}
      {label && (
        <p className="flex items-center gap-2 text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-4 ml-1">
          {BgIcon && <BgIcon size={14} />}
          {label}
        </p>
      )}
      {/* Content */}
      <div>{children}</div>
    </motion.section>
  );
}

function ProjectCard({ p }: { p: typeof PROJECTS[0] }) {
  return (
    <motion.div variants={cardV}>
      <a href={p.url} target="_blank" rel="noopener noreferrer" className="group block relative h-full overflow-hidden rounded-xl border border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:shadow-lg">
        <div className="absolute left-0 top-0 h-full w-1.5 rounded-l-xl" style={{ backgroundColor: p.color }} />
        <div className="pl-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ backgroundColor: `${p.color}15` }}>
              <p.icon size={22} style={{ color: p.color }} />
            </div>
            <ExternalLink size={14} className="text-gray-300 group-hover:text-gray-500 transition" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 group-hover:text-violet-600 transition-colors mb-1">{p.name}</h3>
          <p className="text-sm text-gray-500 mb-3 leading-relaxed">{p.desc}</p>
          <div className="flex items-center gap-4 mb-3">
            {p.stats.map((s) => (
              <div key={s.l} className="text-center">
                <p className="text-lg font-bold" style={{ color: p.color }}>{s.v}</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider">{s.l}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {p.stack.map((t) => (
              <span key={t} className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-medium">{t}</span>
            ))}
          </div>
        </div>
      </a>
    </motion.div>
  );
}

// ====== PAGINA ======
export default function EsteiraPage() {
  const [stats, setStats] = useState<Stats>({ lessons: 141, articles: 29, documents: 19, executions: 24 });
  const [courses, setCourses] = useState<CourseData[]>([]);
  const [flows, setFlows] = useState<FlowData[]>([]);
  const [blogs, setBlogs] = useState<BlogData[]>([]);

  useEffect(() => {
    fetch("/api/hub-data").then(r => r.json()).then(data => {
      if (data.stats) setStats(data.stats);
      if (data.courses?.length) setCourses(data.courses);
      if (data.flows?.length) setFlows(data.flows);
      if (data.blogs?.length) setBlogs(data.blogs);
    }).catch(() => {});
  }, []);

  const allCourses = courses.length > 0 ? courses : [
    { slug: "arquitetura-produtos-educacionais", title: "Arquitetura de Produtos Educacionais", modules: 12, lessons: 67, audio: 0 },
    { slug: "vibecoding-pro", title: "Vibecoding PRO", modules: 9, lessons: 44, audio: 44 },
    { slug: "pre-vibecoding", title: "Pre-Vibecoding", modules: 6, lessons: 30, audio: 30 },
  ];

  const allFlows = flows.length > 0 ? flows : [
    { name: "Entrevista Estruturada", slug: "m1-entrevista", steps: 4, credits_cost: 21000, active: true },
    { name: "Segmentos de Mercado", slug: "m1-segmentos", steps: 3, credits_cost: 14500, active: true },
    { name: "Persona Dissecada", slug: "m1-persona", steps: 3, credits_cost: 17600, active: true },
    { name: "Passaporte de Contexto", slug: "m1-passaporte", steps: 3, credits_cost: 21000, active: true },
  ];

  return (
    <div className="min-h-screen bg-[#faf7f5]">
      <div className="max-w-5xl mx-auto px-4 py-12">

        {/* ===== HERO ===== */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp} className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <Image src="/logo.png" alt="eBuz" width={80} height={80} className="rounded-xl" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">Esteira de Produtos eBuz</h1>
          <p className="text-gray-500 max-w-2xl mx-auto mb-6">
            Do conhecimento tácito ao produto educacional validado. Cada peça desta esteira foi construída com metodologia APE e inteligência artificial.
          </p>
          <div className="flex gap-3 justify-center">
            <Link href="/hub" className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg">Esteira</Link>
            <Link href="/sdd" className="px-4 py-2 border border-gray-300 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition">SDD</Link>
            <Link href="https://github.com/ebuz-ia/ebuz-docs" target="_blank" className="px-4 py-2 border border-gray-300 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition flex items-center gap-1">
              <Github size={14} /> Repo <ExternalLink size={10} />
            </Link>
          </div>
        </motion.div>

        {/* ===== QUICK STATS ===== */}
        <motion.div variants={stagger} initial="hidden" animate="visible" className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-16">
          {[
            { v: stats.lessons, l: "Aulas", icon: GraduationCap },
            { v: allCourses.length, l: "Cursos", icon: BookOpen },
            { v: allFlows.length, l: "Fluxos IA", icon: Layers },
            { v: stats.articles, l: "Artigos", icon: FileText },
            { v: stats.documents, l: "Documentos", icon: FileCode2 },
            { v: stats.executions, l: "Execuções", icon: TrendingUp },
          ].map((s) => (
            <motion.div key={s.l} variants={cardV} className="p-3 rounded-xl border border-gray-200 bg-white text-center hover:shadow-md transition-shadow">
              <s.icon size={18} className="text-violet-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-gray-900">{s.v}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">{s.l}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* ===== JORNADA APE (Framework Visual) ===== */}
        <Section number="00" title="Jornada APE" desc="O framework universal que guia toda a esteira. 50 anos de pesquisa científica, 12 pesquisadores, 5 nos do sistema." label="FRAMEWORK UNIVERSAL" bgIcon={BookOpen}>
          <div className="flex flex-col md:flex-row items-stretch gap-0 mb-8">
            {JOURNEY_STEPS.map((step, idx) => (
              <div key={step.phase} className="flex items-center">
                <div className="flex flex-col items-center justify-center p-4 rounded-xl border border-gray-200 bg-white text-center min-w-[130px] hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${step.color}15` }}>
                    <step.icon size={20} style={{ color: step.color }} />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: step.color }}>{step.phase}</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">{step.label}</p>
                  <p className="text-[10px] text-gray-400 mt-1">{step.desc}</p>
                </div>
                {idx < JOURNEY_STEPS.length - 1 && (
                  <ArrowRight size={18} className="text-gray-300 mx-1 hidden md:block shrink-0" />
                )}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {RESEARCHERS.map((r) => (
              <Link key={r.name} href={r.href} className="group block p-2.5 rounded-lg border border-gray-200 bg-white hover:border-violet-300 hover:shadow-md transition-all">
                <p className="text-sm font-semibold text-gray-900 group-hover:text-violet-600 transition-colors">{r.name}</p>
                <p className="text-[10px] text-gray-500">{r.desc}</p>
                <span className="inline-block mt-1 px-1.5 py-0.5 bg-violet-50 text-violet-600 rounded text-[10px] font-semibold">{r.node}</span>
              </Link>
            ))}
          </div>
        </Section>

        {/* ===== INPUT: ATRACAO ===== */}
        <Section number="01" title="Atração (INPUT)" desc="Blog otimizado para SEO com 29 artigos publicados em 11 categorias. eBook gratuito como isca de captura. Conteúdo que conscientiza antes de vender." label="BLOG & CONTEÚDO" bgIcon={FileText}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <motion.div variants={cardV} className="p-5 rounded-xl border border-blue-200 bg-blue-50/50 md:col-span-2">
              <div className="flex items-center gap-3 mb-3">
                <FileText size={20} className="text-blue-600" />
                <h3 className="font-bold text-gray-900">Blog eBuz</h3>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">{stats.articles} artigos</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">Artigos otimizados para SEO em 11 categorias: Metodologia APE, Andragogia, Design Instrucional, Monetização, Neurociência e mais.</p>
              {blogs.length > 0 && (
                <div className="space-y-2">
                  {blogs.slice(0, 3).map((b) => (
                    <Link key={b.slug} href={`/blog/${b.slug}`} className="block text-sm text-blue-600 hover:underline truncate">
                      &rarr; {b.title}
                    </Link>
                  ))}
                </div>
              )}
              <Link href="/blog" className="inline-flex items-center gap-1 text-sm text-blue-600 font-medium mt-3 hover:underline">
                Ver todos os artigos <ArrowRight size={14} />
              </Link>
            </motion.div>
            <motion.div variants={cardV}>
              <Link href="/ebook" className="group block p-5 rounded-xl border border-gray-200 bg-white hover:border-violet-200 hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-3">
                  <BookMarked size={20} className="text-violet-600" />
                  <ArrowRight size={14} className="text-gray-300 group-hover:text-violet-500 transition" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-violet-600 transition-colors">eBook Gratuito</h3>
                <p className="text-sm text-gray-500 mb-3">Amostra do Livro APE como isca de captura de leads.</p>
                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full text-xs font-semibold">Isca de entrada</span>
              </Link>
            </motion.div>
          </div>
        </Section>

        {/* ===== GATEKEEPER: QUALIFICACAO ===== */}
        <Section number="02" title="Qualificação (GATEKEEPER)" desc="Funil evergreen com webinário de 45 minutos, evento presencial e diagnóstico de perfil. Da captura via Meta Ads até a oferta final." label="FUNIL DE VENDAS & EVENTO" bgIcon={UserCheck}>
          <div className="space-y-4">
            {/* Funil Evergreen - Featured */}
            <motion.div variants={cardV}>
              <div className="relative overflow-hidden rounded-xl border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-white p-6">
                <div className="absolute top-3 right-3">
                  <span className="px-2 py-0.5 bg-amber-600 text-white text-xs font-bold rounded-full">EVERGREEN</span>
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-amber-100">
                    <TrendingUp size={28} className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Funil de Vendas Evergreen</h3>
                    <p className="text-sm text-gray-500 mt-1">O funil que transforma tráfego frio em alunos do curso. Da captura via Meta Ads até o webinário de 45 minutos e a oferta final.</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { step: "1", label: "Captura", desc: "Landing page + Meta Ads", href: "/captura" },
                    { step: "2", label: "Aquecimento", desc: "Sequência de emails", href: "" },
                    { step: "3", label: "Webinário", desc: "45 min de conteúdo + oferta", href: "/webinario" },
                    { step: "4", label: "Fechamento", desc: "Checkout transparente com cartão, PIX e boleto", href: "/oferta" },
                  ].map((s) => {
                    const inner = (
                      <div className={`p-3 rounded-lg border border-amber-100 bg-white text-center ${s.href ? "hover:border-amber-300 hover:shadow-md transition-all" : ""}`}>
                        <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center mx-auto mb-1">{s.step}</div>
                        <p className="text-xs font-semibold text-gray-900">{s.label}</p>
                        <p className="text-[10px] text-gray-400">{s.desc}</p>
                      </div>
                    );
                    return s.href ? (
                      <Link key={s.step} href={s.href}>{inner}</Link>
                    ) : (
                      <div key={s.step}>{inner}</div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-gray-400 mt-3 ml-1">Página de obrigado pós-cadastro: <Link href="/obrigado" className="text-amber-600 hover:underline">/obrigado</Link></p>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <motion.div variants={cardV}>
                <a href="/evento" className="group block p-5 rounded-xl border border-amber-200 bg-amber-50/50 hover:shadow-md transition-all">
                  <Calendar size={20} className="text-amber-600 mb-3" />
                  <h3 className="font-bold text-gray-900 mb-1 group-hover:text-amber-600 transition-colors">Evento Presencial</h3>
                  <p className="text-sm text-gray-500">Workshop de 1 dia. O expert sai com seu Mapa de Contexto pronto. Gatekeeper presencial.</p>
                  <span className="inline-block mt-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">Abril 2026</span>
                </a>
              </motion.div>
              <motion.div variants={cardV}>
                <a href="https://wa.me/5561999969213?text=Ol%C3%A1!%20Gostaria%20de%20agendar%20meu%20Diagn%C3%B3stico%20de%20Perfil%20com%20a%20equipe%20eBuz." target="_blank" rel="noopener noreferrer" className="group block p-5 rounded-xl border border-red-200 bg-red-50/50 hover:shadow-md transition-all">
                  <Target size={20} className="text-red-500 mb-3" />
                  <h3 className="font-bold text-gray-900 mb-1 group-hover:text-red-600 transition-colors">Diagnóstico de Perfil</h3>
                  <p className="text-sm text-gray-500">Chamada de 30 min com estrutura de 4 blocos: Contexto, Diagnóstico, Devolutiva e Próximos Passos.</p>
                  <span className="inline-block mt-2 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Agendar via WhatsApp</span>
                </a>
              </motion.div>
            </div>
          </div>
        </Section>

        {/* ===== BLACK BOX: TRANSFORMACAO ===== */}
        <Section number="03" title="Transformação (BLACK BOX)" desc="3 cursos publicados com 141 aulas e 42h43 de carga horária total. Mapa de Contexto com 4 etapas de IA. Livro APE com 14 capítulos e 12 pesquisadores. O motor que transforma conhecimento tácito em produto." label="CURSOS, MAPA DE CONTEXTO & LIVRO" bgIcon={GraduationCap}>
          {/* Cursos */}
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Cursos Publicados</h3>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
            {allCourses.map((c) => {
              const colors: Record<string, string> = { "vibecoding-pro": "#8B5CF6", "pre-vibecoding": "#3B82F6", "arquitetura-produtos-educacionais": "#10B981" }; const color = colors[c.slug] || "#6B7280";
              const hours = COURSE_HOURS[c.slug];
              return (
                <motion.div key={c.slug} variants={cardV}>
                  <Link href={`/app/curso/${c.slug}`} className="group block relative h-full overflow-hidden rounded-xl border border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:shadow-md">
                    <div className="absolute left-0 top-0 h-full w-1 rounded-l-xl" style={{ backgroundColor: color }} />
                    <div className="pl-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: `${color}15` }}>
                          <GraduationCap size={20} style={{ color }} />
                        </div>
                        {hours && (
                          <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            Total: {hours.totalH}
                          </span>
                        )}
                      </div>
                      <h3 className="mb-1 text-base font-semibold text-gray-900 group-hover:text-violet-600 transition-colors">{c.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                        <span>{c.modules} módulos</span>
                        <span className="w-1 h-1 rounded-full bg-gray-300" />
                        <span>{c.lessons} aulas</span>
                      </div>
                      {hours && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                          <span className="flex items-center gap-1"><Headphones size={11} /> {hours.audioH} áudio</span>
                          <span className="flex items-center gap-1"><BookOpen size={11} /> {hours.leituraH} leitura</span>
                          <span className="flex items-center gap-1"><FileText size={11} /> {hours.exerciciosH} exercícios</span>
                        </div>
                      )}
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Mapa de Contexto - Produto com 4 etapas */}
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Mapa de Contexto (Produto com IA)</h3>
          <motion.div variants={cardV} initial="hidden" whileInView="visible" viewport={{ once: true }} className="rounded-xl border-2 border-violet-200 bg-gradient-to-br from-violet-50/50 to-white p-6 mb-8">
            <div className="flex items-center gap-4 mb-5">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100">
                <MapPin size={24} className="text-violet-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Mapa de Contexto</h3>
                <p className="text-sm text-gray-500">4 etapas com IA que transformam conhecimento tácito em documentos estratégicos. ~35 minutos.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-0">
              {[
                { step: 1, name: "Entrevista Estruturada", desc: "Chat com Jhow IA define o que você ensina, habilidades e público", color: "#EF4444", tag: "IA Chat" },
                { step: 2, name: "Segmentos de Mercado", desc: "Identifica e valida os segmentos mais promissores do seu nicho", color: "#10B981", tag: "IA Fluxo" },
                { step: 3, name: "Persona Dissecada", desc: "Constrói o perfil detalhado do seu cliente ideal com dor real", color: "#8B5CF6", tag: "IA Fluxo" },
                { step: 4, name: "Passaporte de Contexto", desc: "Documento final que consolida todo o contexto estratégico", color: "#F59E0B", tag: "Documento" },
              ].map((etapa, idx) => (
                <div key={etapa.step} className="flex items-center">
                  <div className="w-full p-3 rounded-xl border border-gray-200 bg-white">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: etapa.color }}>
                        {etapa.step}
                      </div>
                      <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: `${etapa.color}15`, color: etapa.color }}>{etapa.tag}</span>
                    </div>
                    <h4 className="text-xs font-semibold text-gray-900">{etapa.name}</h4>
                    <p className="text-[10px] text-gray-400 mt-1 leading-relaxed">{etapa.desc}</p>
                  </div>
                  {idx < 3 && <ArrowRight size={14} className="text-gray-300 mx-1 shrink-0 hidden md:block" />}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Livro APE */}
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Livro APE - Fundamento Científico</h3>
          <motion.div variants={cardV} initial="hidden" whileInView="visible" viewport={{ once: true }} className="p-6 rounded-xl border border-violet-200 bg-violet-50/30">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen size={24} className="text-violet-600" />
              <div>
                <h3 className="font-bold text-gray-900">Arquitetura de Produtos Educacionais</h3>
                <p className="text-xs text-gray-500">14 capítulos, 12 pesquisadores, 50+ anos de pesquisa integrados</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1 mb-4">
              {["INPUT", "GATEKEEPER", "BLACK BOX", "OUTPUT", "FEEDBACK"].map((n, i) => (
                <span key={n}>
                  <span className="px-2 py-0.5 bg-violet-100 rounded text-violet-700 text-xs font-semibold">{n}</span>
                  {i < 4 && <span className="text-violet-300 mx-1 text-xs">&rarr;</span>}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { v: "12", l: "Pesquisadores" },
                { v: "50+", l: "Anos de pesquisa" },
                { v: "14", l: "Capítulos" },
                { v: "5", l: "Nós do framework" },
              ].map((s) => (
                <div key={s.l} className="p-2 rounded-lg bg-white border border-violet-100 text-center">
                  <p className="text-xl font-bold text-violet-600">{s.v}</p>
                  <p className="text-[10px] text-gray-500">{s.l}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
              <Link href="/livro" className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition">
                <ShoppingCart size={16} /> Página de Vendas
              </Link>
              <a href="/livro-ape.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-violet-300 text-violet-600 text-sm font-medium rounded-lg hover:bg-violet-50 transition">
                <Download size={16} /> Download do Livro (PDF)
              </a>
            </div>
          </motion.div>
        </Section>

        {/* ===== OUTPUT: PROJETOS CONSTRUÍDOS ===== */}
        <Section number="04" title="Resultados (OUTPUT)" desc="4 plataformas digitais construídas e operando em produção. eBuz, Negócios S/A, Fonte das Licitações e Pequeno Astronauta. Cada uma é prova viva do método aplicado." label="PROJETOS CONSTRUÍDOS" bgIcon={Rocket}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PROJECTS.map((p) => <ProjectCard key={p.name} p={p} />)}
          </motion.div>
        </Section>

        {/* ===== INFRAESTRUTURA ===== */}
        <Section number="05" title="Infraestrutura" desc="Stack completa de produção: Vercel para deploy, Supabase para banco, Hetzner VPS para serviços, AI Router com fallback automático entre 4 provedores." label="STACK DE PRODUÇÃO" bgIcon={Server}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {INFRA.map((item) => (
              <motion.div key={item.name} variants={cardV} className="p-4 rounded-xl border border-gray-200 bg-white hover:shadow-md transition-shadow">
                <item.icon size={20} className={`${item.color} mb-2`} />
                <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </Section>

        {/* ===== FOOTER ===== */}
        <div className="mt-8 pt-8 border-t border-gray-200 flex flex-wrap gap-4 justify-center text-sm text-gray-400">
          <Link href="/hub" className="hover:text-violet-600 transition">Esteira</Link>
          <Link href="/sdd" className="hover:text-violet-600 transition">SDD</Link>
          <Link href="/blog" className="hover:text-violet-600 transition">Blog</Link>
          <Link href="/app/entrevista" className="hover:text-violet-600 transition">Entrevista</Link>
          <Link href="/captura" className="hover:text-violet-600 transition">Captura</Link>
          <Link href="/webinario" className="hover:text-violet-600 transition">Webinário</Link>
          <Link href="/oferta" className="hover:text-violet-600 transition">Oferta</Link>
          <Link href="https://github.com/ebuz-ia/ebuz-docs" target="_blank" className="hover:text-violet-600 transition flex items-center gap-1">GitHub <ExternalLink size={10} /></Link>
        </div>
      </div>
    </div>
  );
}
